import { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import api, { errMsg } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Zap } from "lucide-react";

const LEVELS = ["beginner", "elementary", "intermediate", "upper_intermediate", "advanced", "proficient"];

const MILESTONE_TEMPLATES = [
  { title: "N-400 vocabulary practice", title_hu: "N-400 szókincs gyakorlás", category: "citizenship" },
  { title: "100 civic questions mastered", title_hu: "100 állampolgársági kérdés elsajátítva", category: "citizenship" },
  { title: "Mock naturalization interview", title_hu: "Próba állampolgársági interjú", category: "citizenship" },
  { title: "Reading & writing test ready", title_hu: "Olvasási és írásbeli tesztre kész", category: "citizenship" },
  { title: "Small talk confidence", title_hu: "Könnyed társalgás", category: "interview" },
  { title: "Workplace email writing", title_hu: "Munkahelyi e-mail írás", category: "interview" },
  { title: "Mock job interview", title_hu: "Próba állásinterjú", category: "interview" },
];

export default function StudentDetail({ studentId, onChanged }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [lessonForm, setLessonForm] = useState({ date: "", duration_min: 60, topic: "", teams_link: "", booking_id: "" });
  const [milestoneForm, setMilestoneForm] = useState({ title: "", title_hu: "" });

  const load = () => api.get(`/admin/students/${studentId}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [studentId]);

  if (!data) return <p className="p-4 text-muted-foreground">{t("common.loading")}</p>;
  const { student, lessons, milestones, bookings, quiz_attempts } = data;

  const patchStudent = async (payload) => {
    try { await api.patch(`/admin/students/${studentId}`, payload); load(); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };

  const logLesson = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/lessons", {
        student_ids: [studentId],
        date: lessonForm.date,
        duration_min: Number(lessonForm.duration_min) || 60,
        topic: lessonForm.topic,
        teams_link: lessonForm.teams_link,
        booking_id: lessonForm.booking_id,
      });
      toast.success(t("admin.lessonLogged"));
      setLessonForm({ date: "", duration_min: 60, topic: "", teams_link: "", booking_id: "" });
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const patchLesson = async (id, payload) => {
    try { await api.patch(`/admin/lessons/${id}`, payload); load(); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };

  const addMilestone = async (title, title_hu, category = "general") => {
    try {
      await api.post(`/admin/students/${studentId}/milestones`, { title, title_hu, category });
      toast.success(t("admin.milestoneAdded"));
      setMilestoneForm({ title: "", title_hu: "" });
      load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const approvedBookings = bookings.filter((b) => b.status === "approved" && b.lessons_remaining > 0);

  return (
    <div className="mt-4 space-y-6" data-testid={`student-detail-${studentId}`}>
      <Card>
        <CardContent className="p-4 space-y-3">
          <p className="text-sm text-muted-foreground">{student.email}{student.phone ? ` · ${student.phone}` : ""}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("admin.levelLabel")}</Label>
              <Select value={student.level || "beginner"} onValueChange={(v) => patchStudent({ level: v })}>
                <SelectTrigger data-testid="student-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{t(`levels.items.${l}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("admin.goalLabel")}</Label>
              <Input defaultValue={student.goal || ""} onBlur={(e) => e.target.value !== (student.goal || "") && patchStudent({ goal: e.target.value })} data-testid="student-goal-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("admin.notesLabel")}</Label>
            <Textarea rows={2} defaultValue={student.notes || ""} onBlur={(e) => e.target.value !== (student.notes || "") && patchStudent({ notes: e.target.value })} data-testid="student-notes-input" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-serif font-semibold">{t("admin.logLesson")}</h3>
          <form onSubmit={logLesson} className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t("admin.date")}</Label>
                <Input type="datetime-local" required value={lessonForm.date} onChange={(e) => setLessonForm({ ...lessonForm, date: e.target.value })} data-testid="lesson-date-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("admin.duration")}</Label>
                <Input type="number" min="15" value={lessonForm.duration_min} onChange={(e) => setLessonForm({ ...lessonForm, duration_min: e.target.value })} data-testid="lesson-duration-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("admin.topic")}</Label>
              <Input value={lessonForm.topic} onChange={(e) => setLessonForm({ ...lessonForm, topic: e.target.value })} data-testid="lesson-topic-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("admin.teamsLink")}</Label>
              <Input placeholder="https://teams.microsoft.com/..." value={lessonForm.teams_link} onChange={(e) => setLessonForm({ ...lessonForm, teams_link: e.target.value })} data-testid="lesson-teams-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("admin.linkLessonToBooking")}</Label>
              <Select value={lessonForm.booking_id || "none"} onValueChange={(v) => setLessonForm({ ...lessonForm, booking_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="lesson-booking-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("admin.noneOption")}</SelectItem>
                  {approvedBookings.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.package_label} ({b.lessons_remaining} {t("admin.remainingLessons").toLowerCase()})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" size="sm" data-testid="log-lesson-submit">{t("admin.logLesson")}</Button>
          </form>

          <div className="mt-4 space-y-3">
            {lessons.map((l) => (
              <div key={l.id} className="border border-border rounded-lg p-3 space-y-2" data-testid={`admin-lesson-${l.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold">{new Date(l.date).toLocaleString()} · {l.duration_min} min</p>
                  <Badge variant={l.status === "completed" ? "secondary" : "outline"} data-testid={`admin-lesson-status-${l.id}`}>{l.status}</Badge>
                </div>
                {l.topic && <p className="text-xs text-muted-foreground">{l.topic}</p>}
                <div className="flex flex-wrap gap-2">
                  {l.status !== "completed" && (
                    <Button size="sm" variant="secondary" onClick={() => patchLesson(l.id, { status: "completed", attended: true })} data-testid={`lesson-complete-${l.id}`}>
                      {t("admin.markCompleted")}
                    </Button>
                  )}
                </div>
                <div className="grid gap-2">
                  <Input placeholder={t("admin.notesLabel")} defaultValue={l.notes || ""} onBlur={(e) => e.target.value !== (l.notes || "") && patchLesson(l.id, { notes: e.target.value })} data-testid={`lesson-notes-${l.id}`} />
                  <Input placeholder={t("admin.homeworkLabel")} defaultValue={l.homework || ""} onBlur={(e) => e.target.value !== (l.homework || "") && patchLesson(l.id, { homework: e.target.value })} data-testid={`lesson-homework-${l.id}`} />
                </div>
              </div>
            ))}
            {lessons.length === 0 && <p className="text-sm text-muted-foreground">{t("admin.noData")}</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-serif font-semibold">{t("dash.milestones")}</h3>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {MILESTONE_TEMPLATES.map((mt) => (
              <Button key={mt.title} size="sm" variant="outline" className="text-xs h-7" onClick={() => addMilestone(mt.title, mt.title_hu, mt.category)} data-testid={`milestone-template-${mt.category}-${mt.title.slice(0, 10).replace(/\s/g, "-")}`}>
                <Zap className="w-3 h-3 mr-1" /> {mt.title}
              </Button>
            ))}
          </div>
          <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (milestoneForm.title.trim()) addMilestone(milestoneForm.title.trim(), milestoneForm.title_hu.trim()); }}>
            <Input placeholder={t("admin.addMilestone")} value={milestoneForm.title} onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })} data-testid="milestone-title-input" />
            <Input placeholder="HU" className="w-32" value={milestoneForm.title_hu} onChange={(e) => setMilestoneForm({ ...milestoneForm, title_hu: e.target.value })} data-testid="milestone-title-hu-input" />
            <Button type="submit" size="sm" data-testid="add-milestone-submit">{t("admin.add")}</Button>
          </form>
          <ul className="mt-3 space-y-2">
            {milestones.map((m) => (
              <li key={m.id} className="flex items-center gap-2.5" data-testid={`admin-milestone-${m.id}`}>
                <Checkbox checked={m.done} onCheckedChange={() => api.patch(`/admin/milestones/${m.id}`).then(() => { load(); onChanged?.(); }).catch((e) => toast.error(errMsg(e)))} data-testid={`milestone-checkbox-${m.id}`} />
                <span className={`text-sm flex-1 ${m.done ? "line-through text-muted-foreground" : ""}`}>{m.title}{m.title_hu ? ` · ${m.title_hu}` : ""}</span>
                <button onClick={() => api.delete(`/admin/milestones/${m.id}`).then(load)} className="text-muted-foreground hover:text-destructive" data-testid={`milestone-delete-${m.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-serif font-semibold">{t("dash.quizScores")}</h3>
          <ul className="mt-2 space-y-1.5">
            {quiz_attempts.length === 0 && <p className="text-sm text-muted-foreground">{t("admin.noData")}</p>}
            {quiz_attempts.map((a) => (
              <li key={a.id} className="flex items-center justify-between text-sm border-b border-border pb-1.5">
                <span>{a.quiz_title} <span className="text-xs text-muted-foreground">· {new Date(a.created_at).toLocaleDateString()}</span></span>
                <Badge variant="outline">{a.score}/{a.total}</Badge>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
